/**
 * Role presentation for user-management surfaces.
 *
 * Lives outside the page component because three surfaces render the same role
 * (the users table, the pending-invites table, and the invite modals) and they
 * must not drift.
 *
 * ## Client mode
 *
 * A pure-client 3PL org has exactly one login type. Under the hood it is an
 * ordinary `Admin` membership — client-ness is a property of the ORGANISATION
 * (`isPureClient`, derived from the `location_client_organizations` junction),
 * never of the user — but calling it "Admin" invites a client to wonder which
 * other roles exist. So the label reads "Client".
 *
 * Two deliberate visual choices:
 *
 * - Admin's normal badge is `destructive` (red). Red is reserved for destructive
 *   actions (DESIGN.md §1), and painting a client org's ONLY login type red reads
 *   as an error state. Client mode uses a neutral variant.
 * - It does NOT reuse ClientOrgChip's brand purple. "Client" already means
 *   "another organisation at this shared location" in this product (DESIGN.md
 *   §19.3/§19.4); making the person badge look like the org chip would merge two
 *   different referents.
 */
export type RoleBadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

/** The single login type shown inside a pure-client org. */
export const CLIENT_ROLE_LABEL = 'Client';

/**
 * The membership role a client-mode invite/create actually writes. Relabelled in
 * the UI, unchanged in the database — no new role string, no RLS/migration work.
 */
export const CLIENT_MODE_ROLE = 'Admin';

/** What to show for a membership/invite role, honouring client mode. */
export function roleDisplayLabel(role: string, isClientMode: boolean): string {
  if (isClientMode && role === CLIENT_MODE_ROLE) return CLIENT_ROLE_LABEL;
  return role;
}

export function roleBadgeVariant(role: string, isClientMode: boolean): RoleBadgeVariant {
  if (isClientMode && role === CLIENT_MODE_ROLE) return 'secondary';
  switch (role) {
    case 'Admin':
      return 'destructive';
    case 'Warehouse Coordinator':
      return 'default';
    default:
      return 'secondary';
  }
}
